import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { take } from 'rxjs';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from '@angular/material/tooltip';
import { CONSTANTS } from "../../../../../Constants/Constants";
import { TranslatePipe } from "@ngx-translate/core";
import { SolPropertiesService } from "../../../../../services/properties/sol-properties.service";
import { DropDownItem } from "../../../../../models/dtos/Classes/DropDownItem";
import { IInstallTypeFormGroup } from "../../../../../models/properties/IInstallTypeFormGroup ";
import { TaxTypes } from "../../../../../models/dtos/Enums/TaxTypes";
import { CommonDataService } from "../../../../../services/common-data.service";
import { IFireTerminationFormGroup } from "../../../../../models/properties/IFireTerminationFormGroup";
import { TaxDetails } from "../../../../../models/dtos";
import { InstallTypeId, TaxRateIndex } from "../../../../../models/enum";
import { NumericMaskDirective } from "../../../../../shared/numeric/numeric-mask.directive";
import { NumericFieldConfig } from "../../../../../shared/numeric/numeric.types";
import { CommonFunctionsService } from "../../../../../services/common-functions.service";
import { APPLY_IN_GUIDE_VIEW_CONFIG_KEYS } from "../../../../../models/properties/ApplyInGuideViewConfigKeys";
@Component({
    selector: 'app-sol-properties-estimate-install-type',
    templateUrl: './sol-properties-estimate-install-type.component.html',
    styleUrls: ['./sol-properties-estimate-install-type.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatCheckboxModule,
        MatRadioModule,
        MatSelectModule,
        MatTooltipModule,
        MatInputModule,
        TranslatePipe,
        NumericMaskDirective,
    ]
})
export class SolPropertiesEstimateInstallTypeComponent implements OnInit {
    installTypeForm: FormGroup<IInstallTypeFormGroup>;
    decisionToolLink: string = CONSTANTS.DECISION_TOOL_LINK;
    readonly fireTerminationForm: FormGroup<IFireTerminationFormGroup> = new FormGroup({
        firePanelTermination: new FormControl(false),
        padPanelTermination: new FormControl(false),
        remoteAnnunciatorTermination: new FormControl(false),
        devicesTermination: new FormControl(false),
        vesdaDetectorTermination: new FormControl(false),
        audioVisualDevicesTermination: new FormControl(false),
        endOfLineTermination: new FormControl(false),
        combinedSpeakerStrobeTermination: new FormControl(false),
        flameBeamDetectorTermination: new FormControl(false),
    });
    private destroyRef = inject(DestroyRef);
    private previousTaxType: number;
    private previousInstallType: number;

    readonly materialAndLaborRateConfig: NumericFieldConfig = {
    kind: 'percentage',
    scale: CONSTANTS.TWO,
    min: CONSTANTS.ZERO,
    max: CONSTANTS.MATERIAL_RATE_INSTALL_TYPE_MAX,
    step: CONSTANTS.ZERO_POINT_ONE,
    useGrouping: false,
    revertToLastValidValue: true,
  };

    constructor(private readonly fb: NonNullableFormBuilder,
        private readonly solPropertiesService: SolPropertiesService,
        private readonly commonDataService: CommonDataService,
        private readonly commonFunctionsService: CommonFunctionsService
    ) { }

    ngOnInit(): void {
        this.initializeForm();
        this.subscribeFormValueChanges();
        this.setupReadOnlyMode();
    }

    private subscribeFormValueChanges(): void {
        this.installTypeForm.valueChanges
            .pipe(take(CONSTANTS.ONE))
            .subscribe(
                () => this.solPropertiesService.markPropertiesChanged()
            );
    }

    private setupReadOnlyMode(): void {
        this.commonDataService.readOnlyMode$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(isReadOnly => {
                if (isReadOnly) {
                    this.installTypeForm.disable({ emitEvent: false });
                } else {
                    this.updateInstallTypeControls();
                }
            });
    }

    private initializeForm(): void {
        const estimateData = this.solPropertiesService.getEstimateData();
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        const taskBasedLaborSettings = this.solPropertiesService.getTaskBasedLaborSettings();
        const otherMetaData = this.solPropertiesService.getOtherMetaDataModel();
        const materialRate = otherMetaData.taxRateCollection.length > CONSTANTS.ZERO ? otherMetaData.taxRateCollection[0]?.taxRate ?? CONSTANTS.ZERO : CONSTANTS.ZERO;
        const laborRate = otherMetaData.taxRateCollection.length > CONSTANTS.ONE ? otherMetaData.taxRateCollection[1]?.taxRate ?? CONSTANTS.ZERO : CONSTANTS.ZERO;
        this.installTypeForm = this.fb.group({
            installType: this.fb.control<number>(estimateSettings.installTypeID ?? CONSTANTS.ONE),
            applyInGuideView: this.fb.control<boolean>(taskBasedLaborSettings?.applyInGuideView ?? false),
            taxType: this.fb.control<number>(estimateData.taxTypeID ?? CONSTANTS.ONE),
            materialRate: this.fb.control<number>(materialRate),
            laborRate: this.fb.control<number>(laborRate),
            sellPrice: this.fb.control<boolean>(estimateSettings.isSellPriceWithTax),
            providedByOthersMech: this.fb.control<boolean>(estimateSettings.mechRateProvidedByOther),
            providedByOthersElec: this.fb.control<boolean>(estimateSettings.elecRateProvidedByOther)
        });
        this.registerFormChangeHandlers();
        this.cleanupProvidedByOthersControls();
        this.updateInstallTypeControls();
        this.initializeFireTerminationForm();
    }

    private initializeFireTerminationForm(): void {


        const taskBasedLaborSettings = this.solPropertiesService.getTaskBasedLaborSettings();
        if (!taskBasedLaborSettings) {
            return;
        }
        this.fireTerminationForm.setValue({
            firePanelTermination: taskBasedLaborSettings.firePanelTermination ?? false,
            padPanelTermination: taskBasedLaborSettings.padPanelTermination ?? false,
            remoteAnnunciatorTermination: taskBasedLaborSettings.remoteAnnunciatorTermination ?? false,
            devicesTermination: taskBasedLaborSettings.devicesTermination ?? false,
            vesdaDetectorTermination: taskBasedLaborSettings.vesdaDetectorTermination ?? false,
            audioVisualDevicesTermination: taskBasedLaborSettings.audioVisualDevicesTermination ?? false,
            endOfLineTermination: taskBasedLaborSettings.endOfLineTermination ?? false,
            combinedSpeakerStrobeTermination: taskBasedLaborSettings.combinedSpeakerStrobeTermination ?? false,
            flameBeamDetectorTermination: taskBasedLaborSettings.flameBeamDetectorTermination ?? false,
        }, { emitEvent: false });

        this.fireTerminationForm.disable({ emitEvent: false });
    }

    /**
     * Cleans up PBO (Provided by Others) form controls when they should be hidden.
     * For security estimates, these controls should not persist any values on save.
     */
    private cleanupProvidedByOthersControls(): void {
        if (this.isSecurityEstimate) {
            this.installTypeForm.controls.providedByOthersMech.setValue(false, { emitEvent: false });
            this.installTypeForm.controls.providedByOthersElec.setValue(false, { emitEvent: false });
        }
    }

    private registerFormChangeHandlers(): void {
        const estimateData = this.solPropertiesService.getEstimateData();
        const estimateSettings = this.solPropertiesService.getEstimateSettings();
        const otherMetaData = this.solPropertiesService.getOtherMetaDataModel();
        this.previousTaxType = estimateData.taxTypeID;
        this.previousInstallType = this.installTypeForm.controls.installType.value;
        this.installTypeForm.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                // Clean up PBO controls before syncing to model when security estimate
                this.cleanupProvidedByOthersControls();

                const currentInstallType = this.installTypeForm.controls.installType.value;
                if (currentInstallType !== this.previousInstallType) {
                    this.previousInstallType = currentInstallType;
                    this.syncApplyInGuideViewFromInstallType(currentInstallType);
                }

                const values = this.installTypeForm.getRawValue();
                estimateSettings.installTypeID = values.installType;
                estimateData.taxTypeID = values.taxType;
                estimateSettings.taxTypeID = values.taxType;
                estimateSettings.isSellPriceWithTax = values.sellPrice;
                estimateSettings.mechRateProvidedByOther = values.providedByOthersMech;
                estimateSettings.elecRateProvidedByOther = values.providedByOthersElec;
                const taskBasedLaborSettings = this.solPropertiesService.getTaskBasedLaborSettings();
                if (taskBasedLaborSettings) {
                    taskBasedLaborSettings.applyInGuideView = values.applyInGuideView;
                }
                const selectedTaxType = this.getSelectedTaxType(values.taxType);
                const taxTypeChanged = values.taxType !== this.previousTaxType;
                this.previousTaxType = values.taxType;

                if (taxTypeChanged) {
                    const materialDefault = Number(selectedTaxType[TaxRateIndex.Material]?.percentage ?? CONSTANTS.ZERO);
                    this.materialRateControl.setValue(materialDefault, { emitEvent: false });
                    const laborDefault = Number(selectedTaxType[TaxRateIndex.Labor]?.percentage ?? CONSTANTS.ZERO);
                    this.laborRateControl.setValue(laborDefault, { emitEvent: false });
                }

                const currentMaterialRate = Number(this.materialRateControl.value ?? CONSTANTS.ZERO);
                const currentLaborRate = Number(this.laborRateControl.value ?? CONSTANTS.ZERO);

                if (values.taxType === TaxTypes.NoTax) {
                    otherMetaData.taxRateCollection = [];
                } else if (values.taxType === TaxTypes.SalesTax) {
                    otherMetaData.taxRateCollection = [
                        {
                            description: selectedTaxType[TaxRateIndex.Material]?.description ?? '',
                            taxRate: currentMaterialRate,
                            saTaxRate: Number(selectedTaxType[TaxRateIndex.Material]?.percentage ?? CONSTANTS.ZERO)
                        },
                        {
                            description: selectedTaxType[TaxRateIndex.Labor]?.description ?? '',
                            taxRate: currentLaborRate,
                            saTaxRate: Number(selectedTaxType[TaxRateIndex.Labor]?.percentage ?? CONSTANTS.ZERO)
                        }
                    ];
                } else {
                    otherMetaData.taxRateCollection = [
                        {
                            description: selectedTaxType[TaxRateIndex.Material]?.description ?? '',
                            taxRate: currentMaterialRate,
                            saTaxRate: Number(selectedTaxType[TaxRateIndex.Material]?.percentage ?? CONSTANTS.ZERO)
                        }
                    ];
                }
 
                this.updateInstallTypeControls();
            });
    }

    // FormGroup Getters
    get materialRateControl(): FormControl<number> {
        return this.installTypeForm.controls.materialRate as FormControl<number>;
    }

    get laborRateControl(): FormControl<number> {
        return this.installTypeForm.controls.laborRate as FormControl<number>;
    }

    get taxTypeControl(): FormControl<number> {
        return this.installTypeForm.controls.taxType as FormControl<number>;
    }



    //DROPDOWN MASTER DATA and their track by functions
    get installTypes(): DropDownItem[] {
        return this.solPropertiesService.getInstallTypesMasterData();
    }
    trackById(index: number, item: DropDownItem): number {
        return item.id;
    }

    trackByIndex(index: number): number {
        return index;
    }

    get taxTypes(): DropDownItem[] {
        return this.solPropertiesService.getTaxTypesMasterData()?.sort((a, b) => b.id - a.id);
    }

    private getSelectedTaxType(value: number): TaxDetails[] {
        const estimateCultureId = this.commonDataService.getEstimateCultureId();
        const taxDetailsList: TaxDetails[] = this.commonDataService.getPreferenceData()?.taxDetailsPreferences?.taxDetailsList ?? [];
        const taxDetailsOnCultureBasis = taxDetailsList.filter(taxType => taxType.cultureID === estimateCultureId);
        const selectedTaxType = taxDetailsOnCultureBasis.filter(taxType => taxType.taxTypeID === value);
        return selectedTaxType;
    }

    get isFireEstimate(): boolean {
        return this.solPropertiesService.getEstimateData()?.business === CONSTANTS.BUSINESS_FIRE;
    }

    get isSecurityEstimate(): boolean {
        return this.solPropertiesService.getEstimateData()?.business === CONSTANTS.BUSINESS_SECURITY;
    }

    private syncApplyInGuideViewFromInstallType(installTypeId: number): void {
        const configKey = APPLY_IN_GUIDE_VIEW_CONFIG_KEYS[installTypeId as InstallTypeId];
        const applyInGuideView = configKey
            ? this.commonFunctionsService.getGeneralConfigValue(configKey)?.trim().toLowerCase() === 'true'
            : false;

        this.installTypeForm.controls.applyInGuideView.setValue(applyInGuideView, { emitEvent: false });

        const taskBasedLaborSettings = this.solPropertiesService.getTaskBasedLaborSettings();
        if (taskBasedLaborSettings) {
            taskBasedLaborSettings.applyInGuideView = applyInGuideView;
        }
    }

    opportunityIntegrationOptionsVisibility(): boolean {
        return this.taxTypeControl.value != TaxTypes.SalesTax || this.commonDataService.globalPPMmode

    }

    private updateInstallTypeControls(): void {
        const userDefinedInstallType = this.installTypes.find(
            installType => installType.id === InstallTypeId.UserDefined
        );

        const isUserDefinedInstallType =
            this.installTypeForm.controls.installType.value === userDefinedInstallType?.id;

        if (isUserDefinedInstallType) {
            this.installTypeForm.enable({ emitEvent: false });
            this.installTypeForm.controls.installType.disable({ emitEvent: false });
            this.installTypeForm.controls.applyInGuideView.disable({ emitEvent: false });
            this.installTypeForm.controls.providedByOthersMech.disable({ emitEvent: false });
            this.installTypeForm.controls.providedByOthersElec.disable({ emitEvent: false });
            return;
        }

        this.installTypeForm.controls.installType.disable({ emitEvent: false });
        this.installTypeForm.controls.applyInGuideView.disable({ emitEvent: false });
        this.installTypeForm.controls.taxType.disable({ emitEvent: false });
        this.installTypeForm.controls.providedByOthersMech.disable({ emitEvent: false });
        this.installTypeForm.controls.providedByOthersElec.disable({ emitEvent: false });
        this.installTypeForm.controls.sellPrice.disable({ emitEvent: false });

        if (this.showMaterialRate) {
            this.materialRateControl.enable({ emitEvent: false });
        } else {
            this.materialRateControl.disable({ emitEvent: false });
        }

        if (this.showLaborRate) {
            this.laborRateControl.enable({ emitEvent: false });
        } else {
            this.laborRateControl.disable({ emitEvent: false });
        }
    }

    get showMaterialRate(): boolean {
        const taxType = this.taxTypeControl.value;
        return taxType === TaxTypes.SalesTax || taxType === TaxTypes.UseTax || taxType === TaxTypes.HST || taxType === TaxTypes.PST;
    }

    get showLaborRate(): boolean {
        return this.taxTypeControl.value === TaxTypes.SalesTax;
    }

}
